import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'

export interface SectionRow {
  title: string
  page_start: number
  page_end: number
}

export interface FileItem {
  id: string
  name: string
  url: string
}

interface Props {
  sections: SectionRow[]
  files: FileItem[]
  onChange: Function
  loading: boolean
}

const EditSectionsDialog = ({ sections, files, onChange, loading }: Props) => {
  const updateField = (index: number, key: string, value: string) => {
    const next = cloneDeep(sections)
    if (key === 'page_start' || key === 'page_end') {
      const num = parseInt(value, 10)
      ;(next[index] as any)[key] = isNaN(num) ? 0 : num
    } else {
      ;(next[index] as any)[key] = value
    }
    onChange(next)
  }

  const onDelete = (index: number) => {
    const next = sections.filter((_, idx) => idx !== index)
    onChange(next)
  }

  const onAdd = (index: number) => {
    const section = sections[index]
    const newValue =
      (typeof section.page_end === 'number' && !isNaN(section.page_end) ? section.page_end : section.page_start || 0) +
      1
    const newRow = {
      title: '',
      page_start: newValue,
      page_end: newValue,
    }
    const next = [...sections.slice(0, index + 1), newRow, ...sections.slice(index + 1)]
    onChange(next)
  }

  if (sections?.length)
    return (
      <div className="overflow-auto max-h-[60vh] rounded">
        <div className="flex items-center px-3 py-2 bg-white sticky top-0 z-10">
          <span className="text-sm font-medium text-gray-700">Sections ({sections.length})</span>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">#</th>
              <th className="text-left px-3 py-2 font-semibold w-[50vw]">Title</th>
              <th className="text-left px-3 py-2 font-semibold">Page Start</th>
              <th className="text-left px-3 py-2 font-semibold">Page End</th>
              <th className="text-left px-3 py-2 font-semibold w-28">Page Range</th>
              <th className="text-left px-3 py-2 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((s, i) => {
              const range = s.page_start === s.page_end ? s.page_start : `${s.page_start} - ${s.page_end}`
              return (
                <tr key={i} className={i % 2 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 align-center text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2 align-center">
                    <input
                      className="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring focus:ring-sky-200"
                      value={s.title}
                      onChange={(e) => updateField(i, 'title', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 align-center">
                    <input
                      type="number"
                      min={1}
                      className="w-24 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring focus:ring-sky-200"
                      value={s.page_start}
                      onChange={(e) => updateField(i, 'page_start', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 align-center">
                    <input
                      type="number"
                      min={s.page_start}
                      className="w-24 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring focus:ring-sky-200"
                      value={s.page_end}
                      onChange={(e) => updateField(i, 'page_end', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 align-center text-gray-700 whitespace-nowrap">{range}</td>
                  <td className="px-3 py-2 align-center">
                    <div className="flex items-center gap-1">
                      <Tooltip message="Insert Row Below">
                        <div
                          className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
                          onClick={() => onAdd(i)}
                        >
                          <PlusIcon className="w-4 h-4 text-sky-700" />
                        </div>
                      </Tooltip>
                      <Tooltip message="Delete">
                        <div
                          className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded ml-2"
                          onClick={() => onDelete(i)}
                        >
                          <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
                        </div>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  else if (files.length > 0)
    return (
      <div className="space-y-3 p-3">
        <div className="text-sm font-medium text-gray-700">PDFs ({files.length})</div>
        <div className="space-y-2">
          {files.map((item) => {
            return (
              <div key={item.id}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-600 hover:underline text-sm break-all"
                >
                  {item.name}
                </a>
              </div>
            )
          })}
        </div>
      </div>
    )
  else if (loading) return <div className="text-sm text-gray-500">Loading...</div>
  else return <div className="text-sm text-gray-500">Not detected</div>
}

export default EditSectionsDialog
